import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';

const API = process.env.REACT_APP_API_URL || 'https://freshhillsmilk.in/api';

const STATUS_META = {
  pending:    { bg: '#f3f4f6', color: '#6b7280',  label: 'Pending'    },
  confirmed:  { bg: '#ecfdf5', color: '#059669',  label: 'Confirmed'  },
  dispatched: { bg: '#fef3c7', color: '#d97706',  label: 'Dispatched' },
  completed:  { bg: '#dbeafe', color: '#2563eb',  label: 'Completed'  },
  cancelled:  { bg: '#fee2e2', color: '#dc2626',  label: 'Cancelled'  },
};

const STATUSES = ['', 'pending', 'confirmed', 'dispatched', 'completed', 'cancelled'];

const fmt     = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const fmtDate = (d) => d
  ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—';

function adminHeaders() {
  const t = localStorage.getItem('adminToken') || localStorage.getItem('token') || '';
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const Spin = ({ size = 20, color = '#059669' }) => (
  <span style={{
    display: 'inline-block', width: size, height: size,
    border: `2.5px solid ${color}22`, borderTopColor: color,
    borderRadius: '50%', animation: 'ao-spin .7s linear infinite', flexShrink: 0,
  }} />
);

export default function AdminOrders() {
  const [stats,      setStats]      = useState(null);
  const [orders,     setOrders]     = useState([]);
  const [total,      setTotal]      = useState(0);
  const [pages,      setPages]      = useState(1);
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [filterSt,   setFilterSt]   = useState('');
  const [fromDate,   setFromDate]   = useState('');
  const [toDate,     setToDate]     = useState('');
  const [selected,   setSelected]   = useState(null);
  const [detailLoad, setDetailLoad] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/orders/stats`, { headers: adminHeaders() });
      const d = await r.json();
      if (d.success) setStats(d.stats);
    } catch {}
  }, []);

  const loadOrders = useCallback(async (p) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p || page, limit: 20 });
      if (filterSt) params.set('status',    filterSt);
      if (search)   params.set('search',    search);
      if (fromDate) params.set('from_date', fromDate);
      if (toDate)   params.set('to_date',   toDate);
      const r = await fetch(`${API}/admin/orders?${params}`, { headers: adminHeaders() });
      const d = await r.json();
      if (d.success) { setOrders(d.orders); setTotal(d.total); setPages(d.pages); }
    } catch {}
    setLoading(false);
  }, [page, filterSt, search, fromDate, toDate]);

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { loadOrders(page); }, [page, filterSt, fromDate, toDate]);

  const doSearch = () => { setPage(1); loadOrders(1); };

  const openDetail = async (orderNum) => {
    setSelected({ order_number: orderNum, _loading: true });
    setDetailLoad(true);
    try {
      const r = await fetch(`${API}/admin/orders/${orderNum}/items`, { headers: adminHeaders() });
      const d = await r.json();
      if (d.success) setSelected({ ...d.order, items: d.items });
    } catch {}
    setDetailLoad(false);
  };

  const updateStatus = async (orderNum, newStatus) => {
    setUpdatingId(orderNum);
    try {
      const r = await fetch(`${API}/admin/orders/${orderNum}/status`, {
        method: 'PUT',
        headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      const d = await r.json();
      if (d.success) {
        loadOrders(page);
        loadStats();
        if (selected?.order_number === orderNum) setSelected(p => ({ ...p, status: newStatus }));
      }
    } catch {}
    setUpdatingId(null);
  };

  const clearFilters = () => { setSearch(''); setFilterSt(''); setFromDate(''); setToDate(''); setPage(1); };

  const css = `
    @keyframes ao-spin { to { transform:rotate(360deg); } }
    @keyframes ao-fade { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }

    /* Stats */
    .ao-stats{display:grid;grid-template-columns:repeat(2,1fr);gap:.75rem;margin-bottom:1.5rem;}
    @media(min-width:640px){.ao-stats{grid-template-columns:repeat(4,1fr);}}
    @media(min-width:1024px){.ao-stats{grid-template-columns:repeat(7,1fr);}}
    .ao-stat{background:white;border-radius:1rem;padding:.9rem 1rem;border:1px solid #e5e7eb;animation:ao-fade .4s;}
    .ao-stat-lbl{font-size:.68rem;font-weight:700;color:#9ca3af;text-transform:uppercase;letter-spacing:.4px;}
    .ao-stat-val{font-size:1.35rem;font-weight:900;color:#111827;margin-top:.15rem;}
    .ao-stat-val.green{color:#059669;} .ao-stat-val.blue{color:#2563eb;}
    .ao-stat-val.amber{color:#d97706;} .ao-stat-val.red{color:#dc2626;}

    /* Toolbar */
    .ao-toolbar{display:flex;flex-wrap:wrap;gap:.6rem;margin-bottom:1rem;align-items:center;}
    .ao-sw{display:flex;flex:1;min-width:200px;border:1.5px solid #e5e7eb;border-radius:.6rem;overflow:hidden;background:white;}
    .ao-si{flex:1;border:none;outline:none;padding:.55rem .75rem;font-size:.88rem;font-family:'Nunito',sans-serif;}
    .ao-sb{background:#059669;color:white;border:none;padding:.55rem .9rem;cursor:pointer;font-weight:700;font-size:.84rem;font-family:'Nunito',sans-serif;}
    .ao-sel{border:1.5px solid #e5e7eb;border-radius:.6rem;padding:.52rem .75rem;font-size:.84rem;background:white;outline:none;cursor:pointer;font-family:'Nunito',sans-serif;}
    .ao-di{border:1.5px solid #e5e7eb;border-radius:.6rem;padding:.52rem .75rem;font-size:.84rem;background:white;outline:none;font-family:'Nunito',sans-serif;}
    .ao-clr{background:#f3f4f6;border:1.5px solid #e5e7eb;border-radius:.6rem;padding:.52rem .9rem;font-size:.82rem;font-weight:700;cursor:pointer;font-family:'Nunito',sans-serif;color:#374151;}
    .ao-clr:hover{background:#e5e7eb;}

    /* Table card */
    .ao-card{background:white;border-radius:1rem;border:1px solid #e5e7eb;overflow:hidden;}
    .ao-tw{overflow-x:auto;}
    table.ao-tbl{width:100%;border-collapse:collapse;font-size:.84rem;}
    .ao-tbl th{background:#f9fafb;padding:.7rem 1rem;text-align:left;font-size:.7rem;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:.4px;border-bottom:1px solid #e5e7eb;white-space:nowrap;}
    .ao-tbl td{padding:.72rem 1rem;border-bottom:1px solid #f3f4f6;vertical-align:middle;}
    .ao-tbl tr:last-child td{border-bottom:none;}
    .ao-tbl tr:hover td{background:#fafafa;}
    .ao-badge{display:inline-block;font-size:.7rem;font-weight:700;padding:.2rem .62rem;border-radius:9999px;white-space:nowrap;}
    .ao-vbtn{background:#f0fdf4;color:#059669;border:1.5px solid #a7f3d0;border-radius:.5rem;padding:.28rem .65rem;font-size:.74rem;font-weight:700;cursor:pointer;font-family:'Nunito',sans-serif;}
    .ao-vbtn:hover{background:#dcfce7;}
    .ao-ssel{border:1.5px solid #e5e7eb;border-radius:.5rem;padding:.24rem .4rem;font-size:.74rem;background:white;outline:none;cursor:pointer;font-family:'Nunito',sans-serif;}

    /* Pagination */
    .ao-pag{display:flex;align-items:center;justify-content:space-between;padding:.85rem 1rem;border-top:1px solid #f3f4f6;flex-wrap:wrap;gap:.5rem;}
    .ao-pag-info{font-size:.8rem;color:#6b7280;}
    .ao-pag-btns{display:flex;gap:.3rem;flex-wrap:wrap;}
    .ao-pb{border:1.5px solid #e5e7eb;background:white;border-radius:.5rem;padding:.28rem .65rem;font-size:.8rem;font-weight:700;cursor:pointer;font-family:'Nunito',sans-serif;}
    .ao-pb.act{background:#059669;color:white;border-color:#059669;}
    .ao-pb:disabled{opacity:.4;cursor:not-allowed;}

    /* Loading */
    .ao-ld{display:flex;align-items:center;justify-content:center;padding:3rem;gap:.75rem;color:#6b7280;font-size:.88rem;}

    /* Modal */
    .ao-ov{position:fixed;inset:0;background:rgba(0,0,0,.5);backdrop-filter:blur(3px);z-index:500;display:flex;align-items:center;justify-content:center;padding:1rem;}
    .ao-modal{background:white;border-radius:1.25rem;width:100%;max-width:560px;max-height:90vh;overflow-y:auto;box-shadow:0 24px 60px rgba(0,0,0,.22);animation:ao-fade .3s;}
    .ao-mhdr{background:linear-gradient(135deg,#059669,#0891b2);color:white;padding:1.25rem 1.5rem;border-radius:1.25rem 1.25rem 0 0;display:flex;align-items:center;justify-content:space-between;}
    .ao-mhdr h3{margin:0;font-size:1.05rem;font-weight:800;}
    .ao-mcl{background:rgba(255,255,255,.25);border:none;color:white;border-radius:50%;width:1.8rem;height:1.8rem;cursor:pointer;font-size:1rem;display:flex;align-items:center;justify-content:center;}
    .ao-mbody{padding:1.25rem 1.5rem;}
    .ao-dr{display:flex;justify-content:space-between;padding:.48rem 0;border-bottom:1px solid #f3f4f6;font-size:.86rem;}
    .ao-dr:last-child{border:none;}
    .ao-dl{color:#6b7280;font-weight:600;}
    .ao-dv{font-weight:700;color:#111827;text-align:right;}
    .ao-sttl{font-size:.72rem;font-weight:800;color:#374151;text-transform:uppercase;letter-spacing:.5px;margin:1rem 0 .4rem;}
    .ao-itbl{width:100%;border-collapse:collapse;font-size:.82rem;margin-top:.5rem;}
    .ao-itbl th{background:#f9fafb;padding:.45rem .75rem;text-align:left;font-size:.68rem;font-weight:700;color:#6b7280;text-transform:uppercase;border-bottom:1px solid #e5e7eb;}
    .ao-itbl td{padding:.5rem .75rem;border-bottom:1px solid #f3f4f6;}
    .ao-itbl tr:last-child td{border:none;}
    .ao-su{display:flex;align-items:center;gap:.5rem;margin-top:1rem;padding-top:1rem;border-top:1px solid #f3f4f6;}
    .ao-ubtn{background:linear-gradient(135deg,#059669,#0891b2);color:white;border:none;border-radius:9999px;padding:.52rem 1.2rem;font-size:.84rem;font-weight:700;cursor:pointer;font-family:'Nunito',sans-serif;}
    .ao-ubtn:disabled{opacity:.5;cursor:not-allowed;}
  `;

  return (
    <AdminLayout>
      <style>{css}</style>

      {/* Page title */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ fontSize: '1.5rem', fontWeight: 900, color: '#111827', marginBottom: '.2rem' }}>
          📦 Orders Management
        </div>
        <div style={{ fontSize: '.85rem', color: '#6b7280' }}>
          View, filter and manage all customer orders
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="ao-stats">
          {[
            { lbl: 'Total Orders',    val: stats.total_orders,   cls: '' },
            { lbl: "Today's Orders",  val: stats.today_orders,   cls: 'blue' },
            { lbl: "Today's Revenue", val: fmt(stats.today_revenue), cls: 'green' },
            { lbl: 'Total Revenue',   val: fmt(stats.total_revenue), cls: 'green' },
            { lbl: '⏳ Pending',      val: stats.pending,    cls: 'amber' },
            { lbl: '✅ Completed',    val: stats.completed,  cls: 'blue'  },
            { lbl: '❌ Cancelled',    val: stats.cancelled,  cls: 'red'   },
          ].map(s => (
            <div key={s.lbl} className="ao-stat">
              <div className="ao-stat-lbl">{s.lbl}</div>
              <div className={`ao-stat-val ${s.cls}`}>{s.val}</div>
            </div>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="ao-toolbar">
        <div className="ao-sw">
          <input className="ao-si" placeholder="Search order #, name or phone…"
            value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && doSearch()} />
          <button className="ao-sb" onClick={doSearch}>🔍 Search</button>
        </div>
        <select className="ao-sel" value={filterSt}
          onChange={e => { setFilterSt(e.target.value); setPage(1); }}>
          {STATUSES.map(s => (
            <option key={s} value={s}>{s ? STATUS_META[s]?.label : 'All Status'}</option>
          ))}
        </select>
        <input type="date" className="ao-di" value={fromDate}
          onChange={e => { setFromDate(e.target.value); setPage(1); }} title="From date" />
        <input type="date" className="ao-di" value={toDate}
          onChange={e => { setToDate(e.target.value); setPage(1); }} title="To date" />
        <button className="ao-clr" onClick={clearFilters}>✕ Clear</button>
      </div>

      {/* Table */}
      <div className="ao-card">
        {loading ? (
          <div className="ao-ld"><Spin /> Loading orders…</div>
        ) : orders.length === 0 ? (
          <div className="ao-ld">📭 No orders found</div>
        ) : (
          <div className="ao-tw">
            <table className="ao-tbl">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Amount</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(o => {
                  const st = STATUS_META[o.status] || STATUS_META.pending;
                  return (
                    <tr key={o.id}>
                      <td style={{ fontWeight: 800, color: '#059669', fontSize: '.8rem' }}>
                        #{o.order_number}
                      </td>
                      <td style={{ fontWeight: 700 }}>{o.customer_name || '—'}</td>
                      <td style={{ color: '#6b7280', fontSize: '.8rem' }}>{o.customer_phone || '—'}</td>
                      <td style={{ fontWeight: 800 }}>{fmt(o.total_amount)}</td>
                      <td>
                        <span style={{ fontSize: '.7rem', background: '#f3f4f6', padding: '.14rem .48rem', borderRadius: 9999, fontWeight: 700, color: '#374151' }}>
                          {o.order_type || 'online'}
                        </span>
                      </td>
                      <td>
                        <span className="ao-badge" style={{ background: st.bg, color: st.color }}>
                          {st.label}
                        </span>
                      </td>
                      <td style={{ fontSize: '.76rem', color: '#6b7280', whiteSpace: 'nowrap' }}>
                        {fmtDate(o.created_at)}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '.4rem', alignItems: 'center' }}>
                          <button className="ao-vbtn" onClick={() => openDetail(o.order_number)}>
                            👁 View
                          </button>
                          <select className="ao-ssel" value={o.status}
                            onChange={e => updateStatus(o.order_number, e.target.value)}
                            disabled={updatingId === o.order_number}>
                            {Object.keys(STATUS_META).map(s => (
                              <option key={s} value={s}>{STATUS_META[s].label}</option>
                            ))}
                          </select>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && pages > 1 && (
          <div className="ao-pag">
            <div className="ao-pag-info">
              Showing {((page - 1) * 20) + 1}–{Math.min(page * 20, total)} of {total} orders
            </div>
            <div className="ao-pag-btns">
              <button className="ao-pb" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
              {Array.from({ length: Math.min(pages, 7) }, (_, i) => i + 1).map(p => (
                <button key={p} className={`ao-pb${p === page ? ' act' : ''}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="ao-pb" disabled={page === pages} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          </div>
        )}
      </div>

      {/* Order Detail Modal */}
      {selected && (
        <div className="ao-ov" onClick={() => setSelected(null)}>
          <div className="ao-modal" onClick={e => e.stopPropagation()}>
            <div className="ao-mhdr">
              <h3>📦 Order #{selected.order_number}</h3>
              <button className="ao-mcl" onClick={() => setSelected(null)}>✕</button>
            </div>
            <div className="ao-mbody">
              {detailLoad ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                  <Spin size={32} />
                </div>
              ) : (
                <>
                  <div className="ao-sttl">Order Info</div>
                  {[
                    ['Order #',    `#${selected.order_number}`],
                    ['Status',     <span className="ao-badge" style={{ background: STATUS_META[selected.status]?.bg, color: STATUS_META[selected.status]?.color }}>{STATUS_META[selected.status]?.label}</span>],
                    ['Amount',     fmt(selected.total_amount)],
                    ['Type',       selected.order_type || 'online'],
                    ['Placed At',  fmtDate(selected.created_at)],
                  ].map(([l, v]) => (
                    <div className="ao-dr" key={l}>
                      <span className="ao-dl">{l}</span>
                      <span className="ao-dv">{v}</span>
                    </div>
                  ))}

                  <div className="ao-sttl">Customer</div>
                  {[
                    ['Name',    selected.customer_name    || '—'],
                    ['Phone',   selected.customer_phone   || '—'],
                    ['Email',   selected.customer_email   || '—'],
                    ['Address', selected.customer_address || '—'],
                  ].map(([l, v]) => (
                    <div className="ao-dr" key={l}>
                      <span className="ao-dl">{l}</span>
                      <span className="ao-dv">{v}</span>
                    </div>
                  ))}

                  {selected.items?.length > 0 && (
                    <>
                      <div className="ao-sttl">Items</div>
                      <table className="ao-itbl">
                        <thead>
                          <tr><th>Product</th><th>Qty</th><th>Price</th><th>Subtotal</th></tr>
                        </thead>
                        <tbody>
                          {selected.items.map((it, i) => (
                            <tr key={i}>
                              <td style={{ fontWeight: 700 }}>{it.product_name || `#${it.product_id}`}</td>
                              <td>{it.quantity}</td>
                              <td>{fmt(it.price)}</td>
                              <td style={{ fontWeight: 700 }}>{fmt(it.subtotal)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}

                  <div className="ao-su">
                    <span style={{ fontSize: '.82rem', fontWeight: 700, color: '#374151', whiteSpace: 'nowrap' }}>
                      Update Status:
                    </span>
                    <select className="ao-sel" style={{ flex: 1 }}
                      defaultValue={selected.status}
                      onChange={e => updateStatus(selected.order_number, e.target.value)}
                      disabled={!!updatingId}>
                      {Object.keys(STATUS_META).map(s => (
                        <option key={s} value={s}>{STATUS_META[s].label}</option>
                      ))}
                    </select>
                    {updatingId && <Spin />}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}